/**
 * localStorage health: how full it is, and what to drop when it fills up.
 *
 * This module deliberately imports nothing. Every other storage helper depends on it, and it is
 * the thing that has to keep working when a write has just failed.
 *
 * Browsers give an origin about 5MB of localStorage and throw on the write that goes over. Nothing
 * we keep there is irreplaceable — it is all caches and preferences — so the response to a full
 * store is to drop the least recently touched entries and try the write again, not to fail.
 */

/**
 * Every key this app owns is namespaced, so nothing it stores can collide with another app on the
 * same origin — or with a future one.
 */
export const NAMESPACE = 'migrupo:';

export const storageKeys = {
  token: `${NAMESPACE}token`,
  settings: `${NAMESPACE}settings`,
  scrollPreferences: `${NAMESPACE}scroll-preferences`,
  // Smart Auto Scroll calibration runs — see timingRecorder.js. Written only while the Part Timer
  // is switched on, and cleared from its own overlay once the data has been exported.
  timingSessions: `${NAMESPACE}timing-sessions`,
};

// The near-universal localStorage limit. Measured in characters, which is what the browsers count.
export const CHARACTER_BUDGET = 5 * 1024 * 1024;

// Past this we start warning; past RECLAIM_AT_RATIO a write failure triggers a prune
export const WARN_AT_RATIO = 0.75;

// How much of a store to drop when reclaiming. A quarter is enough to make room for a long while
// without throwing away more history than it has to.
const RECLAIM_FRACTION = 0.25;

/**
 * Stores that may be pruned to make room, least valuable first. Each is a map of id -> record with
 * a timestamp, and losing an entry costs a re-learned preference, never real data.
 *
 * Declared here as literals rather than imported from settings.js, so this module stays
 * dependency-free — see the note at the top. Keep in step with settingKeys.
 */
const RECLAIMABLE_STORES = [
  // Play cooldowns: pure cache, expire after 24h anyway
  { key: storageKeys.settings, field: 'song-play-cooldowns', timestampOf: (record) => record },
  // Scroll speed / pace per song: costs the player a re-calibration, so it goes last
  { key: storageKeys.scrollPreferences, field: null, timestampOf: (record) => record?.lastAccessed },
];

/** The key scroll preferences live under, out of the shared settings blob — see scrollSpeedPreferences.js */
export const SCROLL_PREFERENCES_KEY = storageKeys.scrollPreferences;

/**
 * Safari in private mode, and any browser at its limit, throws on setItem. The error's identity
 * differs per engine, hence the spread of checks.
 */
export const isQuotaError = (error) =>
  error instanceof DOMException &&
  (error.code === 22 ||
    error.code === 1014 ||
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED');

/**
 * Characters currently held in localStorage, per key and in total.
 * Keys count towards the quota as well as values, so both are measured.
 */
export const getStorageUsage = () => {
  const keys = [];
  let total = 0;

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      const characters = key.length + (localStorage.getItem(key) || '').length;
      keys.push({ key, characters });
      total += characters;
    }
  } catch (error) {
    return { characters: 0, budget: CHARACTER_BUDGET, ratio: 0, keys: [], available: false };
  }

  return {
    characters: total,
    budget: CHARACTER_BUDGET,
    ratio: total / CHARACTER_BUDGET,
    keys: keys.sort((a, b) => b.characters - a.characters),
    available: true,
  };
};

/** Drops the oldest entries from one map, by whatever timestamp it carries. */
const pruneOldest = (entries, timestampOf, fraction) => {
  const sorted = Object.entries(entries).sort(
    (a, b) => (timestampOf(a[1]) || 0) - (timestampOf(b[1]) || 0),
  );
  const dropCount = Math.max(1, Math.ceil(sorted.length * fraction));
  return Object.fromEntries(sorted.slice(dropCount));
};

/**
 * Makes room by pruning the reclaimable stores in order, stopping as soon as one of them yields
 * something. Returns true if anything was freed, so the caller can retry its write.
 */
export const reclaimSpace = () => {
  for (const store of RECLAIMABLE_STORES) {
    try {
      const raw = localStorage.getItem(store.key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      // Either the whole key is the map, or the map is one field inside it (the settings blob)
      const entries = store.field ? parsed?.[store.field] : parsed;
      if (!entries || typeof entries !== 'object' || Object.keys(entries).length === 0) continue;

      const pruned = pruneOldest(entries, store.timestampOf, RECLAIM_FRACTION);
      const next = store.field ? { ...parsed, [store.field]: pruned } : pruned;

      localStorage.setItem(store.key, JSON.stringify(next));
      return true;
    } catch (error) {
      // A store we can't read or rewrite is one we can't reclaim from; try the next
    }
  }

  return false;
};

/**
 * Writes to localStorage, making room and retrying once if the store is full.
 * @returns {boolean} whether the value was written
 */
export const writeItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaError(error) || !reclaimSpace()) return false;

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (retryError) {
      return false;
    }
  }
};

export const readItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

export const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {}
};

/* ---------------------------------------------------------------------------
 * LEGACY KEY MIGRATION — added 2026-09, safe to delete once adoption is broad
 *
 * Before the `migrupo:` namespace these four keys sat unprefixed on the origin. This moves them
 * across on first load and is a no-op every time after, since the old keys are removed as they go.
 *
 * DO NOT REMOVE THIS until nearly every active user has loaded the app at least once since the
 * release that introduced it — `token` is one of the keys, so deleting it early signs people out
 * and loses their settings. Give it a couple of release cycles; there is no cost to keeping it.
 *
 * This runs at module load rather than from a component, because authService reads the token while
 * *its* module is evaluating. Everything that touches storage imports this module, and an imported
 * module is fully evaluated before its importer's body runs, so the rename always lands first.
 * ------------------------------------------------------------------------- */

const LEGACY_RENAMES = {
  token: storageKeys.token,
  settings: storageKeys.settings,
};

const LEGACY_DARK_THEME_KEY = 'darkTheme';

// Dark mode used to be a key of its own holding the string 'true'. It is a preference like any
// other, so it folds into the settings blob instead of being renamed. Written here as raw JSON
// rather than through settings.js, which imports this module and can't be imported back.
const foldDarkThemeIntoSettings = () => {
  const legacy = readItem(LEGACY_DARK_THEME_KEY);
  if (legacy === null) return;

  try {
    const settings = JSON.parse(readItem(storageKeys.settings) || '{}') || {};
    // Anything already chosen under the new setting is the newer answer
    if (settings['dark-theme'] === undefined) {
      settings['dark-theme'] = legacy === 'true';
      writeItem(storageKeys.settings, JSON.stringify(settings));
    }
  } catch (error) {
    // A settings blob we can't parse is one the app will rebuild anyway
  }

  removeItem(LEGACY_DARK_THEME_KEY);
};

export const migrateLegacyKeys = () => {
  try {
    if (typeof localStorage === 'undefined') return;

    Object.entries(LEGACY_RENAMES).forEach(([legacyKey, namespacedKey]) => {
      const value = readItem(legacyKey);
      if (value === null) return;

      // A value already under the new key was written by this version, so it wins
      if (readItem(namespacedKey) === null) writeItem(namespacedKey, value);
      removeItem(legacyKey);
    });

    foldDarkThemeIntoSettings();
  } catch (error) {
    // Storage being unavailable (Safari private mode) is not a reason to fail to boot
  }
};

migrateLegacyKeys();
